import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { IMAGES, PHONE, PHONE_HREF } from "../site";
import { StatusDot } from "./Flourishes";

const LINKS = [
  { to: "/", label: "HOME", id: "home" },
  { to: "/services", label: "SERVICES", id: "services" },
  { to: "/show", label: "TRUCK SHOW", id: "show" },
  { to: "/gallery", label: "GALLERY", id: "gallery" },
  { to: "/contact", label: "GET A QUOTE", id: "contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-zinc-800/70 bg-black/75 backdrop-blur-xl">
      <div className="relative mx-auto flex h-16 sm:h-20 max-w-[1440px] items-center justify-between px-4 sm:px-8">
        <nav className="hidden md:flex items-center gap-6" aria-label="Main navigation">
          {LINKS.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `${l.id === "contact" ? "hidden 2xl:inline-block " : ""}font-mono text-xs font-bold uppercase tracking-[0.25em] transition-colors duration-300 ${
                  isActive ? "text-volt" : "text-zinc-400 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <Link
          to="/"
          className="absolute left-1/2 -translate-x-1/2 flex items-center gap-3"
          data-testid="navbar-brand-logo"
          onClick={() => setOpen(false)}
        >
          <img src={IMAGES.logo} alt="ETN Customz logo" className="h-10 w-10 sm:h-12 sm:w-12 object-cover clip-corner border border-zinc-700" />
          <span className="hidden lg:block font-display text-2xl sm:text-3xl font-black uppercase italic tracking-tight text-white">
            ETN<span className="text-volt"> Customz</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <span
            data-testid="nav-shop-status"
            className="hidden xl:flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500"
          >
            <StatusDot /> Shop open · East TN
          </span>
          <a
            href={PHONE_HREF}
            data-testid="nav-call-button"
            className="clip-badge flex items-center gap-2 bg-volt px-5 py-2.5 font-display text-lg font-800 font-bold uppercase tracking-wide text-black transition-colors duration-300 hover:bg-volt-soft"
          >
            <Phone size={16} strokeWidth={2.5} />
            {PHONE}
          </a>
        </div>

        <button
          className="md:hidden ml-auto text-white p-2"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-menu-button"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden border-t border-zinc-800/70 bg-black/95"
            aria-label="Mobile navigation"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l) => (
                <NavLink
                  key={l.id}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.id}`}
                  className={({ isActive }) =>
                    `font-display text-3xl font-black uppercase tracking-tight py-2 ${
                      isActive ? "text-volt" : "text-zinc-300"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <a href={PHONE_HREF} data-testid="nav-mobile-call-button" className="clip-badge mt-4 flex w-fit items-center gap-2 bg-volt px-5 py-2.5 font-display text-lg font-bold uppercase text-black">
                <Phone size={16} strokeWidth={2.5} />
                {PHONE}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
